import { type ReactNode, useMemo } from 'react'
import {
  ThemeProvider as StyledThemeProvider,
  StyleSheetManager,
} from 'styled-components'
import {
  ThemeProvider as MuiThemeProvider,
  StyledEngineProvider,
} from '@mui/material'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import SnackbarProvider from './elements/Snackbar/SnackbarProvider'
import { muiTheme, theme } from 'mixtiles-web-common/elements/Theme/photosTheme'
import { type Theme } from '@mui/material'
import { domMax, LazyMotion } from 'motion/react'

import SideMenuProvider from 'components/SideMenu/SideMenuProvider'
import { AuthProvider } from './services/AuthProvider/AuthProvider'
import { ApiProvider } from './api/apiProvider'
import { DialogProvider } from './elements/Dialog/DialogProvider'
import { PricingProvider } from './components/Pricing/PricingProvider'
import { AccountProvider } from './components/Account/AccountProvider'
import EmbeddedLoginProvider from './components/EmbeddedLogin/EmbeddedLoginProvider'
import ProductTypeProvider from './services/ProductTypesManager'
import { useLanguage } from './services/LangProvider'
import { type PricingData } from './components/Pricing/types/PricingProvider.types'
import { RootStoreProvider } from './stores/RootStoreProvider'
import { clearSignedOutUserData } from './services/AuthProvider/clearSignedOutUserData'
import { CartStoreProvider } from './stores/cartStore/CartStoreProvider'
import { CartDrawerProvider } from './features/cart/components/CartDrawerProvider'
import { GooglePhotosProvider } from './services/GooglePhotosProvider'
import ProtectUnsavedPhotoUploads from './components/PhotoBank/ProtectUnsavedPhotoUploads'
import { AiTileGenerationOrchestrator } from './features/ai-gen/components/AiTileGenerationOrchestrator'
import { CartUpsellProducers } from './features/cartUpsells/components/CartUpsellProducers'
import { BookArrivalNoticeSnackbar } from './features/bookArrivalNotice/components/BookArrivalNoticeSnackbar'
import { AbandonedCartSync } from './services/UserCart/abandonedCart/AbandonedCartSync'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import CartFlowClientWrapper from './features/cart/components/CartFlowClientWrapper'

declare module '@mui/styles/defaultTheme' {
  // eslint-disable-next-line @typescript-eslint/no-empty-interface
  interface DefaultTheme extends Theme {}
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    },
  },
})

const isReactQueryDevtoolsEnabled =
  import.meta.env.DEV &&
  import.meta.env.VITE_ENABLE_REACT_QUERY_DEVTOOLS === 'true'

export default function DefaultProviders({
  children,
  initialPricingData,
}: {
  children: ReactNode
  initialPricingData: PricingData
}) {
  const language = useLanguage()
  const themeWithLanguage = useMemo(() => ({ ...theme, language }), [language])

  return (
    <StyleSheetManager enableVendorPrefixes>
      <StyledThemeProvider theme={themeWithLanguage}>
        <MuiThemeProvider theme={muiTheme}>
          <StyledEngineProvider injectFirst>
            <ProductTypeProvider>
              <AuthProvider onSignOut={clearSignedOutUserData}>
                <ApiProvider>
                  <QueryClientProvider client={queryClient}>
                    <>
                      {isReactQueryDevtoolsEnabled && (
                        <ReactQueryDevtools initialIsOpen={false} />
                      )}
                      <SnackbarProvider>
                        <DialogProvider>
                          <RootStoreProvider>
                            <CartStoreProvider>
                              <PricingProvider
                                initialPricingData={initialPricingData}
                              >
                                <CartDrawerProvider>
                                  <AccountProvider>
                                    <LazyMotion features={domMax} strict>
                                      <EmbeddedLoginProvider>
                                        <SideMenuProvider>
                                          <GooglePhotosProvider>
                                            <ProtectUnsavedPhotoUploads>
                                              <AiTileGenerationOrchestrator />
                                              <CartUpsellProducers />
                                              <BookArrivalNoticeSnackbar />
                                              <AbandonedCartSync />
                                              <CartFlowClientWrapper>
                                                {children}
                                              </CartFlowClientWrapper>
                                            </ProtectUnsavedPhotoUploads>
                                          </GooglePhotosProvider>
                                        </SideMenuProvider>
                                      </EmbeddedLoginProvider>
                                    </LazyMotion>
                                  </AccountProvider>
                                </CartDrawerProvider>
                              </PricingProvider>
                            </CartStoreProvider>
                          </RootStoreProvider>
                        </DialogProvider>
                      </SnackbarProvider>
                    </>
                  </QueryClientProvider>
                </ApiProvider>
              </AuthProvider>
            </ProductTypeProvider>
          </StyledEngineProvider>
        </MuiThemeProvider>
      </StyledThemeProvider>
    </StyleSheetManager>
  )
}
